import React from 'react';
import { ReactSpreadsheetImport } from 'react-spreadsheet-import';
import { translations } from '../import/ExcelImporter';
import {
  extractApiErrorMessage,
  stockComparisonService,
  StockComparisonUploadResponse,
} from '../../../../core/modules/stockComparison/services/api';

interface Props {
  isOpen: boolean;
  companyId: number;
  onClose: () => void;
  onUploaded: (result: StockComparisonUploadResponse) => void;
}

// Mismos 4 campos que definimos con Seba: codigo de producto, bodega,
// cantidad, y fecha opcional (si no viene, el backend asume hoy). Nombres
// alternativos pensados para que calcen solos con exports tipicos de ERP en
// espanol e ingles -- el usuario igual puede remapear a mano en el paso de
// "match columns" si el archivo trae headers distintos.
const fields = [
  {
    label: 'Código de producto',
    key: 'productSku',
    fieldType: { type: 'input' as const },
    alternateMatches: ['sku', 'codigo', 'código', 'code', 'product_code', 'producto'],
    validations: [{ rule: 'required' as const, errorMessage: 'El código de producto es requerido.' }],
  },
  {
    label: 'Bodega / sucursal',
    key: 'warehouseLabel',
    fieldType: { type: 'input' as const },
    alternateMatches: ['bodega', 'sucursal', 'almacen', 'almacén', 'warehouse', 'warehouse_code'],
    validations: [{ rule: 'required' as const, errorMessage: 'La bodega es requerida.' }],
  },
  {
    label: 'Cantidad',
    key: 'quantity',
    fieldType: { type: 'input' as const },
    alternateMatches: ['cantidad', 'stock', 'quantity', 'qty'],
    validations: [
      { rule: 'required' as const, errorMessage: 'La cantidad es requerida.' },
      { rule: 'regex' as const, value: '^-?[0-9]+(\\.[0-9]+)?$', errorMessage: 'Debe ser un número válido.' },
    ],
  },
  {
    label: 'Fecha (opcional)',
    key: 'snapshotDate',
    fieldType: { type: 'input' as const },
    alternateMatches: ['fecha', 'date', 'snapshot_date'],
  },
];

const StockComparisonUploadDialog = ({ isOpen, companyId, onClose, onUploaded }: Props) => {
  const handleSubmit = async (data: { validData: any[] }, file: File) => {
    if (!Array.isArray(data.validData) || data.validData.length === 0) {
      alert('El archivo no tiene filas válidas para cargar.');
      onClose();
      return;
    }
    try {
      const items = data.validData.map((row) => ({
        productSku: String(row.productSku ?? '').trim(),
        warehouseLabel: String(row.warehouseLabel ?? '').trim(),
        quantity: Number(row.quantity),
        snapshotDate: row.snapshotDate ? String(row.snapshotDate).trim() : undefined,
      }));
      const result = await stockComparisonService.uploadFile(companyId, items, file?.name);
      onUploaded(result);
    } catch (err: any) {
      alert(extractApiErrorMessage(err, 'No se pudo procesar el archivo. Revisa los datos e intenta de nuevo.'));
    } finally {
      onClose();
    }
  };

  return (
    <ReactSpreadsheetImport
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      fields={fields as any}
      translations={translations}
    />
  );
};

export default StockComparisonUploadDialog;
